import React from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';
import { translationPropType } from '@eb/i18n';
import { ReCaptcha } from 'react-recaptcha-v3';
import isUndefined from 'lodash/isUndefined';

import FormSubmitButton from '../FormSubmitButton';
import PasswordField from '../PasswordField';
import StaticEmailField from '../StaticEmailField';
import CaptchaField from '../CaptchaField';

import { Button } from '@eb/eds-button';
import { formValidate } from '@eb/eds-utils';
import { TYPE_BUTTON, STYLE_ANCHOR } from '@eb/eds-button';

import { logInValidate as validate } from '../utils';
import { LOG_IN_FORM } from '../../constants';
import {
    GA_EVENTBRITE,
    GA_FACEBOOK,
    GA_APPLE,
} from '../../constants/analytics';
import { APPLE_AUTHENTICATION_DATA_PROPTYPES } from '../../constants/proptypes';

const CAPTCHA_POLL_RATE = 100;
const CAPTCHA_RETRY_RATE = 110000;
const CAPTCHA_MAX_WAIT_TIME = 5000;

const ForgotPasswordLink = ({ handleForgotPassword }) => (
    <div className="eds-l-pad-top-2 eds-text--center">
        <Button
            type={TYPE_BUTTON}
            style={STYLE_ANCHOR}
            onClick={handleForgotPassword}
            data-automation="signup-forgot-password-button"
        >
            {gettext('Forgot password')}
        </Button>
    </div>
);

class LogInForm extends React.PureComponent {
    static propTypes = {
        isCheckoutAuthenticationFlow: PropTypes.bool,
        handleSubmit: PropTypes.func.isRequired,
        email: PropTypes.string,
        handleEmailEdit: PropTypes.func,
        buttonCTA: translationPropType,
        handleForgotPassword: PropTypes.func,
        showNotification: PropTypes.func,
        clearNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        disableRecaptcha3: PropTypes.bool,
        showCaptcha: PropTypes.bool,
        trackOnPageLoad: PropTypes.func,
        sourceProvider: PropTypes.string,
        socialData: PropTypes.shape({
            apple: APPLE_AUTHENTICATION_DATA_PROPTYPES,
        }),
        /* Props Provided Via ReduxForm */
        submitFailed: PropTypes.bool,
        submitSucceeded: PropTypes.bool,
        submitting: PropTypes.bool,
    };

    state = {
        enableCaptchaV3: false,
        captchaToken: null,
    };

    componentDidMount() {
        const { trackOnPageLoad } = this.props;

        if (trackOnPageLoad) {
            trackOnPageLoad();
        }
    }

    componentWillReceiveProps({ submitting, submitFailed, error }) {
        const { showNotification, clearNotification } = this.props;

        /*If the form has an error and submit failed, attempt
        to display a notification*/
        if (submitFailed && error) {
            showNotification(error);
        }

        /*We want to limit only one error notification to
        appear per form submission if the user has dismissed it.
        So each time submitting is true, then reset the flag.*/
        if (submitting) {
            clearNotification();
        }
    }

    componentWillUnmount() {
        const { clearNotification } = this.props;

        clearNotification();

        if (this._submitIfCaptchaTimeout) {
            clearTimeout(this._submitIfCaptchaTimeout);
            this._submitIfCaptchaTimeout = null;
        }

        if (this._handleCaptchaV3Timeout) {
            clearTimeout(this._handleCaptchaV3Timeout);
            this._handleCaptchaV3Timeout = null;
        }
    }

    _handleForgotPassword = () => {
        const {
            handleForgotPassword,
            clearNotification,
            socialData: {
                apple: { email: _socialEmailApple } = {},
                facebook: { email: _socialEmailFB } = {},
            } = {},
        } = this.props;
        let { sourceProvider } = this.props;

        if (isUndefined(sourceProvider)) {
            if (_socialEmailApple) {
                sourceProvider = GA_APPLE;
            } else if (_socialEmailFB) {
                sourceProvider = GA_FACEBOOK;
            } else {
                sourceProvider = GA_EVENTBRITE;
            }
        }

        clearNotification();

        handleForgotPassword({
            sourceProvider,
        });
    };

    _handleDelayedSubmit = (event) => {
        event.preventDefault();

        this.setState({
            enableCaptchaV3: true,
        });

        this.captchaStartTime = Date.now();

        const submitIfCaptcha = () => {
            const currentTime = Date.now();

            if (
                this.state.captchaToken ||
                this.captchaStartTime + CAPTCHA_MAX_WAIT_TIME < currentTime ||
                this.props.disableRecaptcha3
            ) {
                this.props.handleSubmit();
            } else {
                this._submitIfCaptchaTimeout = setTimeout(
                    submitIfCaptcha,
                    CAPTCHA_POLL_RATE,
                );
            }
        };

        submitIfCaptcha();
    };

    _handleCaptchaV3 = (captchaToken) => {
        this.setState({
            enableCaptchaV3: false,
            captchaToken,
        });

        this.props.change('recaptchaV3ResponseToken', captchaToken);

        //The recaptcha token expires after two minutes
        this._handleCaptchaV3Timeout = setTimeout(() => {
            this.setState({ enableCaptchaV3: true });
        }, CAPTCHA_RETRY_RATE);
    };

    _handlePasswordInput = () => {
        this.setState({
            enableCaptchaV3: true,
        });
    };

    render() {
        const {
            email,
            handleEmailEdit,
            submitting,
            submitSucceeded,
            buttonCTA,
            clearNotification,
            clearAuthNotification,
            submitFailed,
            showCaptcha,
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            disableRecaptcha3,
            isCheckoutAuthenticationFlow,
        } = this.props;

        const { enableCaptchaV3 } = this.state;

        const buttonClasses =
            'eds-fx--fade-in-down eds-fx--delay-1 eds-l-mar-top-6';

        const isSubmitDisabled = submitting || submitSucceeded;

        const _handleClearNotification = () => {
            clearNotification && clearNotification();
            clearAuthNotification && clearAuthNotification();
        };

        let forgotPasswordLink;

        if (!isCheckoutAuthenticationFlow) {
            forgotPasswordLink = (
                <ForgotPasswordLink
                    handleForgotPassword={this._handleForgotPassword}
                />
            );
        }

        return (
            <div className="eds-fx--fade-in eds-fx--delay-2">
                <form
                    onSubmit={this._handleDelayedSubmit}
                    noValidate="novalidate"
                >
                    <StaticEmailField
                        email={email}
                        onEmailEdit={handleEmailEdit}
                        clearNotification={_handleClearNotification}
                    />
                    <PasswordField
                        isLogin={true}
                        submitFailed={submitFailed}
                        onChange={this._handlePasswordInput}
                        autoFocus={true}
                    />
                    <CaptchaField
                        showCaptcha={showCaptcha}
                        recaptcha2PublicKey={recaptcha2PublicKey}
                        submitFailed={submitFailed}
                    />

                    {enableCaptchaV3 && !disableRecaptcha3 && (
                        <ReCaptcha
                            sitekey={recaptcha3PublicKey}
                            action="login"
                            verifyCallback={this._handleCaptchaV3}
                        />
                    )}

                    <FormSubmitButton
                        submitDisabled={isSubmitDisabled}
                        buttonClasses={buttonClasses}
                        buttonCTA={buttonCTA}
                    />

                    {forgotPasswordLink}
                </form>
            </div>
        );
    }
}

export default formValidate({
    validate,
    form: LOG_IN_FORM,
    touchOnBlur: false,
})(LogInForm);
